import { useState } from "react";
import { useLocation } from "react-router-dom";
import { Phone, Mail, MapPin, Clock, MessageCircle, Music2, Twitter, Youtube, Facebook, Instagram } from "lucide-react";
import { toast } from "sonner";
import { COMPANY, primaryWA, waLink } from "@/lib/site";
import { getProductNameFromPath, trackEvent } from "@/lib/analytics";

const initial = {
  nama: "",
  perusahaan: "",
  whatsapp: "",
  kota: "",
  jenis_produk: "",
  detail: "",
};

const productOptions = [
  "Acrylic Sheet",
  "Baja Ringan & Galvalum",
  "Kitchen Equipment",
  "Elektronik",
];

const socialIconMap = {
  email: Mail,
  instagram: Instagram,
  tiktok: Music2,
  x: Twitter,
  youtube: Youtube,
  facebook: Facebook,
};

export const Contact = () => {
  const [form, setForm] = useState(initial);
  const { pathname } = useLocation();
  const productName = getProductNameFromPath(pathname);
  const productContext = form.jenis_produk || productName || "produk PT. Animax Putra Lancar";

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const buildWaMessage = (f) => `Halo PT. Animax Putra Lancar, saya ingin meminta penawaran ${productContext}:
Nama: ${f.nama}
Perusahaan: ${f.perusahaan || "-"}
WhatsApp: ${f.whatsapp}
Kota: ${f.kota || "-"}
Jenis Produk: ${f.jenis_produk || "-"}
Pesan / Detail Kebutuhan: ${f.detail || "-"}`;

  const onSubmit = (e) => {
    e.preventDefault();
    if (!form.nama || !form.whatsapp) {
      toast.error("Mohon lengkapi Nama dan Nomor WhatsApp");
      return;
    }
    trackEvent("contact_form_submitted", {
      category: "contact",
      label: "Quote Form",
      source: "contact_form",
      product_name: productName,
      sales_name: primaryWA.name,
    });
    window.open(waLink(primaryWA.raw, buildWaMessage(form)), "_blank");
  };

  return (
    <section id="kontak" data-testid="contact-section" className="py-24 lg:py-32 bg-animax-gray">
      <div className="max-w-7xl mx-auto px-5 sm:px-6">
        <div className="max-w-3xl mb-16">
          <div className="tag-uppercase text-animax-orange mb-4">Kontak & Penawaran</div>
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl tracking-tight font-medium text-animax-black">
            Hubungi PT. Animax Putra Lancar
          </h2>
          <p className="mt-4 text-neutral-600 text-lg leading-relaxed">
            Kami siap membantu kebutuhan material dan proyek Anda. Hubungi kami melalui WhatsApp, telepon,
            email, atau kirim permintaan penawaran melalui form di bawah.
          </p>
        </div>

        <div className="grid lg:grid-cols-12 gap-8">
          <div className="lg:col-span-5 space-y-4">
            <div className="p-6 rounded-2xl bg-white border border-neutral-200" data-testid="contact-wa">
              <div className="flex items-start gap-4">
                <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-[#e60012] to-[#ff6873] flex items-center justify-center shrink-0">
                  <MessageCircle className="w-5 h-5 text-white" />
                </div>
                <div className="flex-1">
                  <div className="tag-uppercase text-neutral-500 mb-2">WhatsApp</div>
                  <div className="space-y-2">
                    {COMPANY.whatsapp.map((w, i) => (
                      <div key={i} className="flex items-center justify-between gap-2 min-w-0" data-testid={`contact-wa-row-${i}`}>
                        <a
                          href={waLink(w.raw, `Halo ${w.name}, saya ingin bertanya tentang ${productContext}`)}
                          target="_blank"
                          rel="noopener noreferrer"
                          data-testid={`contact-wa-${i}`}
                          onClick={() =>
                            trackEvent("whatsapp_button_clicked", {
                              category: "contact",
                              label: w.name,
                              source: "contact_section",
                              sales_name: w.name,
                              product_name: productName,
                            })
                          }
                          className="flex-1 min-w-0 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1 group hover:text-animax-orange transition-colors"
                        >
                          <span className="text-animax-black text-sm font-medium break-all">{w.number}</span>
                          <span className="tag-uppercase text-neutral-400 group-hover:text-animax-orange">{w.name}</span>
                        </a>
                        <a
                          href={`tel:${w.tel}`}
                          data-testid={`contact-tel-${i}`}
                          onClick={() =>
                            trackEvent("phone_number_clicked", {
                              category: "contact",
                              label: w.name,
                              source: "contact_section",
                              sales_name: w.name,
                              product_name: productName,
                            })
                          }
                          className="p-1.5 rounded-full bg-neutral-100 hover:bg-gradient-to-br hover:from-[#e60012] hover:to-[#ff6873] transition-colors"
                          aria-label={`Telepon ${w.name}`}
                          title={`Telepon ${w.name}`}
                        >
                          <Phone className="w-3.5 h-3.5 text-animax-black" />
                        </a>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            <div className="p-6 rounded-2xl bg-white border border-neutral-200" data-testid="contact-social-card">
              <div className="tag-uppercase text-neutral-500 mb-4">Email & Social Profiles</div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {COMPANY.socialProfiles.map((profile) => {
                  const Icon = socialIconMap[profile.key] || MessageCircle;
                  return (
                    <a
                      key={profile.key}
                      href={profile.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid={`contact-social-${profile.key}`}
                      className="flex items-center gap-3 rounded-xl border border-neutral-200 bg-neutral-50 px-3 py-3 hover:border-animax-orange hover:bg-white transition-colors"
                    >
                      <span className="w-9 h-9 rounded-lg bg-white border border-neutral-200 flex items-center justify-center shrink-0">
                        <Icon className="w-4 h-4 text-animax-black" />
                      </span>
                      <span className="min-w-0">
                        <span className="block text-sm font-medium text-animax-black">{profile.label}</span>
                        <span className="block text-xs text-neutral-500 truncate">{profile.handle}</span>
                      </span>
                    </a>
                  );
                })}
              </div>
            </div>

            <div className="p-6 rounded-2xl bg-white border border-neutral-200" data-testid="contact-address">
              <div className="flex items-start gap-4">
                <div className="w-11 h-11 rounded-xl bg-neutral-100 flex items-center justify-center shrink-0">
                  <MapPin className="w-5 h-5 text-animax-black" />
                </div>
                <div>
                  <div className="tag-uppercase text-neutral-500 mb-2">Alamat</div>
                  <p className="text-animax-black text-sm leading-relaxed">{COMPANY.address}</p>
                </div>
              </div>
            </div>

            <div className="p-6 rounded-2xl bg-white border border-neutral-200" data-testid="contact-hours">
              <div className="flex items-start gap-4">
                <div className="w-11 h-11 rounded-xl bg-neutral-100 flex items-center justify-center shrink-0">
                  <Clock className="w-5 h-5 text-animax-black" />
                </div>
                <div className="flex-1">
                  <div className="tag-uppercase text-neutral-500 mb-3">Jam Operasional</div>
                  <div className="space-y-1.5">
                    {COMPANY.hours.map((h, i) => (
                      <div key={i} className="grid grid-cols-[minmax(0,1fr)_auto] gap-3 text-xs">
                        <span className="text-neutral-600">{h.day}</span>
                        <span className={`font-medium text-right ${h.time === "Tutup" ? "text-red-500" : "text-animax-black"}`}>{h.time}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            <div className="rounded-2xl overflow-hidden border border-neutral-200 aspect-[16/10]" data-testid="contact-map">
              <iframe
                title="Lokasi PT Animax Putra Lancar"
                src={`https://www.google.com/maps?q=${COMPANY.mapsQuery}&output=embed`}
                width="100%"
                height="100%"
                style={{ border: 0 }}
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              />
            </div>
          </div>

          <div className="lg:col-span-7">
            <form
              onSubmit={onSubmit}
              data-testid="quote-form"
              className="p-5 sm:p-8 lg:p-10 rounded-3xl bg-white border border-neutral-200 shadow-[0_20px_60px_-30px_rgba(0,0,0,0.15)]"
            >
              <h3 className="font-display text-2xl font-medium text-animax-black">Minta Penawaran Harga</h3>
              <p className="mt-2 mb-8 text-sm text-neutral-600 leading-relaxed">
                Isi form berikut, lalu kirim langsung ke WhatsApp tim sales kami.
              </p>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <Field label="Nama *">
                  <input value={form.nama} onChange={(e) => setField("nama", e.target.value)} required
                    className={inputCls} placeholder="Nama lengkap" data-testid="quote-nama-input" />
                </Field>
                <Field label="Nama Perusahaan">
                  <input value={form.perusahaan} onChange={(e) => setField("perusahaan", e.target.value)}
                    className={inputCls} placeholder="Opsional" data-testid="quote-perusahaan-input" />
                </Field>
                <Field label="Nomor WhatsApp *">
                  <input value={form.whatsapp} onChange={(e) => setField("whatsapp", e.target.value)} required
                    className={inputCls} placeholder="+62 812-xxxx-xxxx" data-testid="quote-wa-input" />
                </Field>
                <Field label="Kota">
                  <input value={form.kota} onChange={(e) => setField("kota", e.target.value)}
                    className={inputCls} placeholder="Kota / Kabupaten" data-testid="quote-kota-input" />
                </Field>
                <Field label="Jenis Produk">
                  <select value={form.jenis_produk} onChange={(e) => setField("jenis_produk", e.target.value)}
                    className={inputCls} data-testid="quote-produk-input">
                    <option value="">Pilih produk</option>
                    {productOptions.map((option) => <option key={option}>{option}</option>)}
                  </select>
                </Field>
              </div>

              <div className="mt-5">
                <Field label="Pesan / Detail Kebutuhan">
                  <textarea rows={4} value={form.detail} onChange={(e) => setField("detail", e.target.value)}
                    className={inputCls + " resize-none"}
                    placeholder="Ceritakan kebutuhan Anda: aplikasi, timeline, lokasi pengiriman, atau pertanyaan lainnya."
                    data-testid="quote-detail-input" />
                </Field>
              </div>

              <div className="mt-8">
                <button
                  type="submit"
                  data-testid="quote-send-wa"
                  className="w-full inline-flex items-center justify-center gap-2 px-6 sm:px-7 py-4 rounded-full bg-gradient-to-br from-[#e60012] to-[#ff6873] text-white font-medium text-sm hover:brightness-95 transition-all"
                >
                  <MessageCircle className="w-4 h-4" />
                  Kirim via WhatsApp
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};

const inputCls = "w-full px-4 py-3 rounded-lg border border-neutral-200 bg-white text-sm text-animax-black placeholder:text-neutral-400 focus:outline-none focus:border-animax-orange focus:ring-2 focus:ring-animax-orange/20 transition-all";

const Field = ({ label, children }) => (
  <div>
    <label className="block tag-uppercase text-neutral-500 mb-2">{label}</label>
    {children}
  </div>
);
